/**
 * Certification Submissions - Admin Portal
 * List/Card view toggle with DataTable and Kanban board
 */

import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import ErrorDisplay from '../../../components/ErrorDisplay';
import SearchSelect from '../../../components/SearchSelect';
import DataTable, { StatusBadge, type DataRow } from '../../../components/DataTable';
import type { Column, Filter, FilterOption } from '../../../components/DataTable';

// Status configuration
interface StatusConfigEntry {
  label: string;
  color: string;
  bg: string;
}

const STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  draft: { label: 'Draft', color: 'var(--color-text-light)', bg: 'var(--color-bg)' },
  returned: { label: 'Returned', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  pending_verification: { label: 'Pending Verification', color: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  pending_authorization: { label: 'Pending Authorization', color: '#8b5cf6', bg: '#ede9fe' },
  pending_endorsement: { label: 'Pending Endorsement', color: '#a855f7', bg: '#f3e8ff' },
  certified: { label: 'Certified', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  endorsed: { label: 'Endorsed', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  rejected: { label: 'Rejected', color: 'var(--color-error)', bg: 'var(--color-error-bg)' }
};

// Workflow columns for Kanban board
const WORKFLOW_COLUMNS = [
  { id: 'draft', label: 'Draft' },
  { id: 'returned', label: 'Returned' },
  { id: 'pending_verification', label: 'Verification' },
  { id: 'pending_authorization', label: 'Authorization' },
  { id: 'certified', label: 'Certified' }
];

// Minimum chars on the operator's non-compliance reason — must match
// the gate in CertificationSubmit.tsx so both ends of the form agree.
const NON_COMPLIANCE_REASON_MIN_CHARS = 20;

// Return targets supported by returnSubmission. 'operator' bumps the
// cert back to draft for re-submission; 'verifier' bumps an authorise-
// stage cert back to pending_verification for a fresh verifier review.
type ReturnStage = 'operator' | 'verifier';

/**
 * Inspect the operator's checklist for contradictions that would make a
 * verify unsafe. Returns the list of human-readable issues; an empty
 * array means the checklist is internally consistent.
 *
 * Contradictions that block verify:
 * 1. Any line-item question (steps 3-6) marked "no" with no comment —
 *    operator flagged a failure but didn't explain it. Verifier must
 *    return for clarification before approving.
 * 2. Final Declaration marked non-compliant but the reason is missing
 *    or under the minimum length — pre-Unit-1 submissions could land
 *    in this shape (Philip Palmgren cert was the canonical example).
 */
function detectCertContradictions(checklist: Record<string, Record<string, unknown>>): string[] {
  const issues: string[] = [];
  for (const stepKey of ['step3', 'step4', 'step5', 'step6']) {
    const step = checklist[stepKey];
    if (!step) continue;
    for (const key of Object.keys(step)) {
      if (!key.startsWith('q') || key.endsWith('_comment')) continue;
      const value = step[key];
      const isNo = value === 'no' || value === false || value === 'false';
      if (!isNo) continue;
      const comment = step[`${key}_comment`];
      const commentEmpty = !comment || (typeof comment === 'string' && comment.trim().length === 0);
      if (commentEmpty) {
        issues.push(`${stepKey}.${key} flagged "No" without a comment`);
      }
    }
  }
  const step8 = checklist.step8;
  if (step8 && step8.protocol_compliance === false) {
    const reason = step8.non_compliance_reason;
    const reasonText = typeof reason === 'string' ? reason.trim() : '';
    if (reasonText.length < NON_COMPLIANCE_REASON_MIN_CHARS) {
      issues.push(`Final Declaration flagged non-compliant but reason is missing or under ${NON_COMPLIANCE_REASON_MIN_CHARS} characters`);
    }
  }
  return issues;
}

/**
 * Validate a freshly-fetched cert_submissions row before calling
 * verify on it. Returns an error message (string) when verify should
 * be blocked, or null when verify is safe to proceed.
 */
function validateForVerify(row: Record<string, unknown>): string | null {
  if (row.submission_status !== 'pending_verification') {
    return `Submission is currently '${String(row.submission_status)}', not pending verification. Refresh and try again.`;
  }
  if (!row.operator_signed_at) {
    return 'Operator has not signed this submission yet. Verify is blocked until the operator signs.';
  }
  const checklist = (row.checklist_data || {}) as Record<string, Record<string, unknown>>;
  const issues = detectCertContradictions(checklist);
  if (issues.length > 0) {
    return `Cannot verify — contradictions found:\n\n• ${issues.join('\n• ')}\n\nReturn the submission to the operator for clarification first.`;
  }
  return null;
}

interface Submission extends DataRow {
  id: string;
  service_date: string;
  operator_id: string;
  service_location: string;
  submission_status: string;
  created_at: string;
  invoice_number: string;
  customer_name: string;
  room_id: string | null;
  operator_name: string;
  display_customer: string;
}

interface SubmissionDetail {
  [key: string]: unknown;
  id?: string;
  loading?: boolean;
  error?: string;
  operator_name?: string;
  verifier_name?: string | null;
  authorizer_name?: string | null;
  endorser_name?: string | null;
  submission_status?: string;
  service_date?: string;
  service_location?: string;
  customer_name?: string;
  invoice_number?: string;
  created_at?: string;
  verified_by?: string;
  verified_at?: string;
  verification_notes?: string;
  authorized_by?: string;
  authorized_at?: string;
  authorization_notes?: string;
  endorsed_by?: string;
  endorsed_at?: string;
  endorsement_notes?: string;
  equipment_used?: Record<string, unknown>[];
  primary_asset_description?: string;
  additional_items_description?: string;
  sundries?: Record<string, unknown>[];
  checklist_data?: Record<string, Record<string, unknown>>;
  return_reason?: string;
  returned_at?: string;
  returned_by?: string;
  room_id?: string;
  operator_id?: string;
  audit_entries?: AuditEntry[];
}

interface AuditEntry {
  id: string;
  user_id?: string | null;
  user_name?: string;
  action: string;
  field_path?: string;
  before_value?: unknown;
  after_value?: unknown;
  reason?: string | null;
  created_at: string;
}

interface AdminUser {
  id: string;
  name: string;
  canVerify: boolean;
  canAuthorize: boolean;
}

/** boardOnly: the Dashboard's Certificates tab shows the board alone, without the view toggle. */
function Certification({ boardOnly = false }: { boardOnly?: boolean } = {}) {
  const { supabase, user, hasPermission, isSuperAdmin, getScope } = useAuth();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('certification');

  // Permissions - map to Sentinel's permission levels
  const canVerify = hasPermission('certification', 'manage');
  // Authorise is restricted to super-admins and country admins (global scope)
  // and never to the same person who verified the submission. The same-person
  // check is per-row (see canAuthorizeRow).
  const canAuthorize = isSuperAdmin || getScope() === 'global';
  const canAuthorizeRow = (verifiedBy: string | null | undefined) =>
    canAuthorize && verifiedBy !== user?.id;

  // Data state
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin users who can verify/authorize
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);

  // Batch selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchVerifierId, setBatchVerifierId] = useState<string | null>(null);
  const [batchAuthorizerId, setBatchAuthorizerId] = useState<string | null>(null);

  // View mode: 'list' or 'card' - saved to localStorage
  const [viewMode, setViewMode] = useState(() => {
    if (boardOnly) return 'card';
    return localStorage.getItem('certViewMode') || 'list';
  });

  // Modal state
  const [viewingSubmission, setViewingSubmission] = useState<SubmissionDetail | null>(null);

  // Save view mode preference
  useEffect(() => {
    if (!boardOnly) localStorage.setItem('certViewMode', viewMode);
  }, [viewMode, boardOnly]);

  // Load admin users who can verify/authorize
  useEffect(() => {
    async function loadAdminUsers() {
      try {
        const { data, error: err } = await supabase
          .from('users')
          .select('id, first_name, last_name, permissions')
          .eq('status', 'active')
          .order('first_name');

        if (err) throw err;

        // Eligibility uses the level-rank model — matches `check_rls_access`
        // inside the verify/authorize RPCs. The boolean
        // `certification.verify` / `certification.authorize` fields the
        // earlier filter looked for don't exist in the schema and would
        // have silently excluded every real manager from the dropdown.
        const LEVEL_RANK: Record<string, number> = { off: 0, view: 1, contribute: 2, manage: 3 };
        const MANAGE_RANK = 3;
        type AdminPerms = { super_admin?: boolean };
        type CertPerms = { level?: string };
        type UserPerms = { admin?: AdminPerms; certification?: CertPerms };

        const eligible = ((data || []) as Record<string, unknown>[]).filter(u => {
          const perms = (u.permissions || {}) as UserPerms;
          if (perms.admin?.super_admin === true) return true;
          const rank = LEVEL_RANK[perms.certification?.level || 'off'] ?? 0;
          return rank >= MANAGE_RANK;
        }).map(u => {
          const perms = (u.permissions || {}) as UserPerms;
          const isSuper = perms.admin?.super_admin === true;
          const rank = LEVEL_RANK[perms.certification?.level || 'off'] ?? 0;
          const hasManage = rank >= MANAGE_RANK;
          return {
            id: u.id as string,
            name: `${u.first_name} ${u.last_name}`.trim(),
            canVerify: isSuper || hasManage,
            canAuthorize: isSuper // Authorise is restricted to super-admins / global-scope users; mirror the RPC.
          };
        });

        setAdminUsers(eligible);
      } catch (err) {
        console.error('Error loading admin users:', err);
      }
    }

    loadAdminUsers();
  }, [supabase]);

  // Set default batch verifier/authorizer when admin users load or user changes
  useEffect(() => {
    if (user?.id && !batchVerifierId) {
      setBatchVerifierId(user.id);
    }
    if (user?.id && !batchAuthorizerId) {
      setBatchAuthorizerId(user.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only set defaults when user/adminUsers load, not when batch IDs change
  }, [user?.id, adminUsers]);

  // Load data on mount
  useEffect(() => {
    loadSubmissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount
  }, []);

  async function loadSubmissions() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: err } = await supabase
        .from('cert_submissions')
        .select(`
          id,
          service_date,
          operator_id,
          service_location,
          submission_status,
          created_at,
          invoice_number,
          customer_name,
          room_id
        `)
        .order('created_at', { ascending: false })
        .limit(10000);

      if (err) throw err;

      // Get operator names
      const operatorIds = [...new Set(((data || []) as Record<string, unknown>[]).map(s => s.operator_id as string).filter(Boolean))];
      const operatorMap: Record<string, string> = {};
      if (operatorIds.length > 0) {
        const { data: operators } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', operatorIds);
        ((operators || []) as Record<string, unknown>[]).forEach(op => {
          operatorMap[op.id as string] = `${op.first_name} ${op.last_name}`.trim();
        });
      }

      const enriched = ((data || []) as Record<string, unknown>[]).map(sub => ({
        ...sub,
        operator_name: operatorMap[sub.operator_id as string] || 'Unknown',
        display_customer: (sub.customer_name as string) || (sub.service_location as string)?.split(' - ')[0] || '\u2014'
      })) as Submission[];

      setSubmissions(enriched);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // Extract unique operators for filter dropdown
  const operatorOptions = useMemo((): FilterOption[] => {
    const operators = new Map<string, string>();
    submissions.forEach(sub => {
      if (sub.operator_name && sub.operator_name !== 'Unknown') {
        operators.set(sub.operator_name, sub.operator_name);
      }
    });
    return Array.from(operators.values()).sort().map(name => ({
      value: name,
      label: name
    }));
  }, [submissions]);

  // Status filter options
  const statusOptions = useMemo((): FilterOption[] => {
    return Object.entries(STATUS_CONFIG).map(([key, config]) => ({
      value: key,
      label: config.label
    }));
  }, []);

  // Group submissions by status for Kanban board
  const submissionsByStatus = useMemo(() => {
    const grouped: Record<string, Submission[]> = {};
    WORKFLOW_COLUMNS.forEach(col => {
      grouped[col.id] = [];
    });
    submissions.forEach(sub => {
      const bucket = grouped[sub.submission_status];
      if (bucket) {
        bucket.push(sub);
      }
    });
    return grouped;
  }, [submissions]);

  // View submission details
  async function viewSubmission(id: string) {
    setViewingSubmission({ id, loading: true });

    try {
      const { data, error: err } = await supabase
        .from('cert_submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (err) throw err;

      const d = data as Record<string, unknown>;

      // Get operator name
      let operatorName = 'Unknown';
      if (d.operator_id) {
        const { data: op } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', d.operator_id)
          .single();
        if (op) {
          const o = op as Record<string, unknown>;
          operatorName = `${o.first_name} ${o.last_name}`.trim();
        }
      }

      // Get verifier, authorizer, endorser names if present
      const userIds = [d.verified_by, d.authorized_by, d.endorsed_by].filter(Boolean) as string[];
      const userMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', userIds);
        ((users || []) as Record<string, unknown>[]).forEach(u => {
          userMap[u.id as string] = `${u.first_name} ${u.last_name}`.trim();
        });
      }

      // Audit trail — append-only log of admin corrections on this cert.
      // Used for AFSA defensibility ("show me what was edited and by whom").
      const { data: auditRows } = await supabase
        .from('cert_submission_audits')
        .select('id, user_id, action, field_path, before_value, after_value, reason, created_at')
        .eq('submission_id', id)
        .order('created_at', { ascending: true });
      const auditUserIds = ((auditRows || []) as Record<string, unknown>[])
        .map(a => a.user_id as string | null)
        .filter(Boolean) as string[];
      if (auditUserIds.length > 0) {
        const missing = auditUserIds.filter(uid => !userMap[uid]);
        if (missing.length > 0) {
          const { data: auditUsers } = await supabase
            .from('users')
            .select('id, first_name, last_name')
            .in('id', missing);
          ((auditUsers || []) as Record<string, unknown>[]).forEach(u => {
            userMap[u.id as string] = `${u.first_name} ${u.last_name}`.trim();
          });
        }
      }
      const auditEntries = ((auditRows || []) as Record<string, unknown>[]).map(a => ({
        ...a,
        user_name: a.user_id ? (userMap[a.user_id as string] || 'Unknown') : 'System',
      }));

      setViewingSubmission({
        ...d,
        operator_name: operatorName,
        verifier_name: userMap[d.verified_by as string] || null,
        authorizer_name: userMap[d.authorized_by as string] || null,
        endorser_name: userMap[d.endorsed_by as string] || null,
        audit_entries: auditEntries,
      } as SubmissionDetail);
    } catch (err) {
      console.error('Error loading submission:', err);
      setViewingSubmission({ error: (err as Error).message });
    }
  }

  function closeModal() {
    setViewingSubmission(null);
  }

  function editDraft(id: string) {
    window.location.href = `/certification/submit?edit=${id}`;
  }

  async function verifySubmission(id: string, selectedUserId?: string) {
    if (!canVerify) {
      alert('You do not have permission to verify submissions.');
      return;
    }
    const verifierId = selectedUserId || user?.id || '';

    try {
      // Fetch the row so we can validate state + contradictions before
      // we move it. Stops the "pending_authorization" rubber-stamp.
      const { data: fresh, error: fetchErr } = await supabase
        .from('cert_submissions')
        .select('submission_status, operator_signed_at, operator_id, checklist_data')
        .eq('id', id)
        .single();
      if (fetchErr) throw fetchErr;

      const freshRow = fresh as Record<string, unknown>;

      const guardError = validateForVerify(freshRow);
      if (guardError) {
        alert(guardError);
        return;
      }

      // Same-person guard: verifier cannot be the operator. Super-admins
      // can override by selecting another verifier from the dropdown.
      if (verifierId === freshRow.operator_id) {
        alert('The operator cannot verify their own service. A different manager must verify this submission.');
        return;
      }

      if (!confirm('Verify this submission and move to authorization?')) return;

      // Verify via RPC. Server enforces caller permission, operator-as-
      // verifier block, and ignores p_verifier_id unless caller is super-
      // admin. p_verifier_id only carries data on the admin-override path.
      const rpcArgs: { p_submission_id: string; p_verifier_id?: string } = { p_submission_id: id };
      if (selectedUserId && selectedUserId !== user?.id) {
        rpcArgs.p_verifier_id = selectedUserId;
      }
      const { error: err } = await supabase.rpc('verify_cert_submission', rpcArgs);
      if (err) throw err;

      // Update local state optimistically (preserves filters)
      setSubmissions(prev => prev.map(sub =>
        sub.id === id
          ? { ...sub, submission_status: 'pending_authorization' }
          : sub
      ));

      alert('Submission verified successfully!');
      closeModal();
    } catch (err) {
      console.error('Error verifying submission:', err);
      alert('Error verifying submission: ' + (err as Error).message);
    }
  }

  async function authorizeSubmission(id: string, _selectedUserId?: string) {
    if (!canAuthorize) {
      alert('You do not have permission to authorize submissions.');
      return;
    }
    if (!confirm('Authorise this submission and issue the certificate?')) return;

    try {
      // Fetch room_id and service_date for the post-authorise hosp_rooms update
      const { data: submission, error: fetchErr } = await supabase
        .from('cert_submissions')
        .select('room_id, service_date')
        .eq('id', id)
        .single();

      if (fetchErr) throw fetchErr;

      const sub = submission as Record<string, unknown>;

      // Authorise via RPC. Server enforces super-admin / global-scope role
      // and same-person guard. authorized_by is always the caller (auth.uid()).
      const { error: err } = await supabase.rpc('authorize_cert_submission', { p_submission_id: id });
      if (err) throw err;

      // Update the room's last_service_date if this is a POS/hospitality certificate
      if (sub.room_id) {
        const { error: roomErr } = await supabase
          .from('hosp_rooms')
          .update({ last_service_date: sub.service_date })
          .eq('id', sub.room_id);

        if (roomErr) {
          console.error('Error updating room last_service_date:', roomErr);
        }
      }

      // Update local state optimistically (preserves filters)
      setSubmissions(prev => prev.map(s =>
        s.id === id
          ? { ...s, submission_status: 'certified' }
          : s
      ));

      alert('Submission authorized! Certificate issued.');
      closeModal();
    } catch (err) {
      console.error('Error authorizing submission:', err);
      alert('Error authorizing submission: ' + (err as Error).message);
    }
  }

  async function returnSubmission(id: string, stage: ReturnStage) {
    const targetLabel = stage === 'verifier' ? 'the verifier' : 'the operator';
    const reason = prompt(`Please provide a reason for returning this submission to ${targetLabel}:`);
    if (!reason || !reason.trim()) return;

    const newStatus = stage === 'verifier' ? 'pending_verification' : 'returned';
    // Note: verified_by is intentionally kept when returning to verifier
    // so the email notification can resolve the previous verifier as the
    // recipient. The re-verify RPC call overwrites it cleanly anyway.
    const updates: Record<string, unknown> = {
      submission_status: newStatus,
      return_reason: reason.trim(),
      returned_at: new Date().toISOString(),
      returned_by: user?.id
    };

    try {
      const { error: err } = await supabase
        .from('cert_submissions')
        .update(updates)
        .eq('id', id);

      if (err) throw err;

      setSubmissions(prev => prev.map(sub =>
        sub.id === id
          ? { ...sub, submission_status: newStatus }
          : sub
      ));

      // Best-effort email notification. Failure must not block the user
      // from finishing the return action.
      try {
        await supabase.functions.invoke('cert-return-notify', { body: { submission_id: id, stage } });
      } catch (notifyErr) {
        console.warn('cert-return-notify failed:', notifyErr);
      }

      alert(stage === 'verifier' ? 'Submission returned to verifier.' : 'Submission returned to operator.');
      closeModal();
    } catch (err) {
      console.error('Error returning submission:', err);
      alert('Error returning submission: ' + (err as Error).message);
    }
  }

  /**
   * Remove a submission that should never have existed — a duplicate, a wrong
   * customer, a test. Head office only, and only while no certificate has been
   * issued: once a customer holds a certificate the honest action is a revoke,
   * which the RPC enforces rather than trusting this screen.
   */
  async function deleteSubmission(id: string) {
    if (!canAuthorize) {
      alert('You do not have permission to delete submissions.');
      return;
    }

    const reason = prompt('Why is this submission being deleted? This is recorded and cannot be undone:');
    if (!reason || !reason.trim()) return;

    if (!confirm('Delete this submission permanently? The certificate work on it will be lost.')) return;

    try {
      const { error: err } = await supabase.rpc('delete_cert_submission', {
        p_submission_id: id,
        p_reason: reason.trim(),
      });

      if (err) throw err;

      setSubmissions(prev => prev.filter(sub => sub.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      alert('Submission deleted.');
      closeModal();
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert('Error deleting submission: ' + (err as Error).message);
    }
  }

  function viewCertificate(id: string) {
    window.open(`/certificate.html?id=${id}`, '_blank');
  }

  // Batch verify selected submissions
  async function batchVerify() {
    if (!canVerify) {
      alert('You do not have permission to verify submissions.');
      return;
    }

    const candidateIds = [...selectedIds].filter(id => {
      const sub = submissions.find(s => s.id === id);
      return sub?.submission_status === 'pending_verification';
    });

    if (candidateIds.length === 0) {
      alert('No selected items are pending verification.');
      return;
    }

    try {
      // Fetch the candidate rows so we can run the same contradiction
      // and state guards we apply to single-row verify.
      const { data: fetchedRows, error: fetchErr } = await supabase
        .from('cert_submissions')
        .select('id, submission_status, operator_signed_at, operator_id, checklist_data')
        .in('id', candidateIds);
      if (fetchErr) throw fetchErr;

      const verifierId = batchVerifierId || user?.id || '';
      const rows = (fetchedRows || []) as Record<string, unknown>[];
      const okIds: string[] = [];
      const blocked: string[] = [];
      for (const row of rows) {
        const guardError = validateForVerify(row);
        if (guardError) {
          blocked.push(`• ${row.id} — ${guardError.split('\n')[0]}`);
        } else if (row.operator_id === verifierId) {
          blocked.push(`• ${row.id} — operator cannot verify their own service`);
        } else {
          okIds.push(row.id as string);
        }
      }

      if (okIds.length === 0) {
        alert(`No submissions can be verified — every selected row was blocked by a contradiction or wrong state:\n\n${blocked.join('\n')}`);
        return;
      }

      const skippedNote = blocked.length > 0
        ? `\n\n${blocked.length} skipped (return to operator before verifying):\n${blocked.join('\n')}`
        : '';

      if (!confirm(`Verify ${okIds.length} submission(s) and move to authorization?${skippedNote}`)) return;

      // Batch verify each through the same RPC the single-row path uses,
      // so the same-person guard fires server-side per row.
      const failures: string[] = [];
      const successIds: string[] = [];
      for (const okId of okIds) {
        const rpcArgs: { p_submission_id: string; p_verifier_id?: string } = { p_submission_id: okId };
        if (verifierId && verifierId !== user?.id) {
          rpcArgs.p_verifier_id = verifierId;
        }
        const { error: rpcErr } = await supabase.rpc('verify_cert_submission', rpcArgs);
        if (rpcErr) {
          failures.push(`${okId}: ${rpcErr.message}`);
        } else {
          successIds.push(okId);
        }
      }

      setSubmissions(prev => prev.map(sub =>
        successIds.includes(sub.id)
          ? { ...sub, submission_status: 'pending_authorization' }
          : sub
      ));

      setSelectedIds(new Set());
      if (failures.length > 0) {
        alert(`${successIds.length} verified, ${failures.length} failed:\n${failures.join('\n')}`);
      } else {
        alert(`${successIds.length} submission(s) verified successfully!${blocked.length > 0 ? ` (${blocked.length} skipped — see previous message.)` : ''}`);
      }
    } catch (err) {
      console.error('Error batch verifying:', err);
      alert('Error verifying submissions: ' + (err as Error).message);
    }
  }

  // Batch authorize selected submissions
  async function batchAuthorize() {
    if (!canAuthorize) {
      alert('You do not have permission to authorize submissions.');
      return;
    }

    const eligibleIds = [...selectedIds].filter(id => {
      const sub = submissions.find(s => s.id === id);
      return sub?.submission_status === 'pending_authorization';
    });

    if (eligibleIds.length === 0) {
      alert('No selected items are pending authorization.');
      return;
    }

    // Filter out rows the caller verified themselves — RPC will reject those anyway
    const safeIds = eligibleIds.filter(id => {
      const sub = submissions.find(s => s.id === id);
      return sub?.verified_by !== user?.id;
    });
    const skipped = eligibleIds.length - safeIds.length;
    if (safeIds.length === 0) {
      alert('You verified all of the selected submissions. Someone else must authorise them.');
      return;
    }

    if (!confirm(`Authorise ${safeIds.length} submission(s) and issue certificates?${skipped > 0 ? `\n\n${skipped} skipped — you cannot authorise your own verifications.` : ''}`)) return;

    try {
      const eligibleSubs = submissions.filter(s => safeIds.includes(s.id));
      const failures: string[] = [];

      // Authorise each via the RPC (server enforces role + same-person guard)
      for (const id of safeIds) {
        const { error: rpcErr } = await supabase.rpc('authorize_cert_submission', { p_submission_id: id });
        if (rpcErr) {
          failures.push(`${id}: ${rpcErr.message}`);
        }
      }

      // Update room last_service_dates for hospitality certificates that succeeded
      const successIds = safeIds.filter(id => !failures.some(f => f.startsWith(id)));
      for (const sub of eligibleSubs.filter(s => successIds.includes(s.id))) {
        if (sub.room_id && sub.service_date) {
          await supabase
            .from('hosp_rooms')
            .update({ last_service_date: sub.service_date })
            .eq('id', sub.room_id);
        }
      }

      setSubmissions(prev => prev.map(sub =>
        successIds.includes(sub.id)
          ? { ...sub, submission_status: 'certified' }
          : sub
      ));

      setSelectedIds(new Set());
      if (failures.length > 0) {
        alert(`${successIds.length} authorised, ${failures.length} failed:\n${failures.join('\n')}`);
      } else {
        alert(`${successIds.length} submission(s) authorized! Certificates issued.`);
      }
    } catch (err) {
      console.error('Error batch authorizing:', err);
      alert('Error authorizing submissions: ' + (err as Error).message);
    }
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Permission loading state
  if (permissionLoading) {
    return (
      <div className="placeholder-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!canAccess) {
    return <AccessDenied feature="certification" />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={loadSubmissions} feature="certification" />;
  }

  // Columns for DataTable
  const columns: Column[] = [
    {
      key: 'invoice_number',
      label: 'Ref',
      sortable: true,
      render: (row: DataRow) => <strong>{(row.invoice_number as string) || '\u2014'}</strong>
    },
    {
      key: 'service_date',
      label: 'Date',
      sortable: true,
      render: (row: DataRow) => formatDate(row.service_date as string)
    },
    {
      key: 'display_customer',
      label: 'Customer',
      sortable: true
    },
    {
      key: 'operator_name',
      label: 'Operator',
      sortable: true
    },
    {
      key: 'submission_status',
      label: 'Status',
      render: (row: DataRow) => {
        const s = STATUS_CONFIG[row.submission_status as string] ?? { label: 'Draft', color: 'var(--color-text-light)', bg: 'var(--color-bg)' };
        return <StatusBadge bg={s.bg} color={s.color}>{s.label}</StatusBadge>;
      }
    }
  ];

  // Filters for DataTable
  const filters: Filter[] = [
    { key: 'submission_status', label: 'Status', options: statusOptions },
    { key: 'operator_name', label: 'Operator', options: operatorOptions }
  ];

  return (
    <div>
      {/* View Modal */}
      {viewingSubmission && (
        <ViewModal
          submission={viewingSubmission}
          onClose={closeModal}
          onVerify={(selectedUserId: string) => verifySubmission(viewingSubmission.id as string, selectedUserId)}
          onAuthorize={(selectedUserId: string) => authorizeSubmission(viewingSubmission.id as string, selectedUserId)}
          onReturn={(stage: ReturnStage) => returnSubmission(viewingSubmission.id as string, stage)}
          onEdit={() => editDraft(viewingSubmission.id as string)}
          onCertificate={() => viewCertificate(viewingSubmission.id as string)}
          canVerify={canVerify}
          canAuthorize={canAuthorize}
          formatDate={formatDate}
          adminUsers={adminUsers}
          currentUserId={user?.id || ''}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {/* List/Card View Toggle Tabs */}
      {!boardOnly && <div className="page-tabs">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          List View
        </button>
        <button
          className={`page-tab ${viewMode === 'card' ? 'active' : ''}`}
          onClick={() => setViewMode('card')}
        >
          Card View
        </button>
      </div>}

      {/* Batch Actions Bar - shown when items are selected */}
      {selectedIds.size > 0 && (
        <div className="batch-actions-bar">
          <span className="batch-actions-count">{selectedIds.size} selected</span>
          <div className="batch-actions-buttons">
            {canVerify && (
              <>
                <SearchSelect
                  width="inline"
                  options={adminUsers.filter(au => au.canVerify).map(au => ({ value: au.id, label: au.name }))}
                  value={batchVerifierId || user?.id || ''}
                  onChange={setBatchVerifierId}
                />
                <button className="btn btn-secondary btn-sm" onClick={batchVerify}>
                  Batch Verify
                </button>
              </>
            )}
            {canAuthorize && (
              <button className="btn btn-primary btn-sm" onClick={batchAuthorize}>
                Batch Authorise
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={clearSelection}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === 'list' ? (
        <DataTable
          columns={columns}
          data={submissions}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search customer, ref #, operator..."
          searchKeys={['customer_name', 'display_customer', 'invoice_number', 'operator_name', 'service_location']}
          filters={filters}
          defaultSort={{ key: 'created_at', direction: 'desc' }}
          pageSize={50}
          selectable={true}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          headerActions={{
            primary: { label: '+ Certificate', onClick: () => { window.location.href = '/certification/submit'; } },
            onSync: loadSubmissions
          }}
          actions={(row: DataRow) => {
            const isDraft = row.submission_status === 'draft';
            const isReturned = row.submission_status === 'returned';
            const isPendingVerification = row.submission_status === 'pending_verification';
            const isPendingAuthorization = row.submission_status === 'pending_authorization';
            const isCertified = row.submission_status === 'certified';

            return (
              <>
                <span className="action-icon" onClick={(e) => { e.stopPropagation(); viewSubmission(row.id as string); }} title="View submission details">👁️</span>
                {(isDraft || isReturned) && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); editDraft(row.id as string); }} title="Edit draft">✏️</span>
                )}
                {isPendingVerification && canVerify ? (
                  <button
                    className="btn btn-action btn-primary"
                    onClick={(e) => { e.stopPropagation(); verifySubmission(row.id as string); }}
                  >
                    Verify
                  </button>
                ) : isPendingAuthorization && canAuthorizeRow(row.verified_by as string | null | undefined) ? (
                  <button
                    className="btn btn-action btn-primary"
                    onClick={(e) => { e.stopPropagation(); authorizeSubmission(row.id as string); }}
                  >
                    Authorise
                  </button>
                ) : isCertified ? (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); viewCertificate(row.id as string); }} title="View certificate">📜</span>
                ) : null}
                {canAuthorize && !isCertified && (
                  <span
                    className="action-icon action-icon--danger"
                    onClick={(e) => { e.stopPropagation(); deleteSubmission(row.id as string); }}
                    title="Delete submission"
                  >
                    🗑️
                  </span>
                )}
              </>
            );
          }}
          onRowClick={(row: DataRow) => viewSubmission(row.id as string)}
          emptyMessage="No submissions match your filters"
        />
      ) : (
        <BoardView
          submissionsByStatus={submissionsByStatus}
          onView={viewSubmission}
          onEdit={editDraft}
          onVerify={verifySubmission}
          onAuthorize={authorizeSubmission}
          onCertificate={viewCertificate}
          onDelete={deleteSubmission}
          canVerify={canVerify}
          canAuthorize={canAuthorize}
          canAuthorizeRow={canAuthorizeRow}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// Kanban Board View Component
interface BoardViewProps {
  submissionsByStatus: Record<string, Submission[]>;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onVerify: (id: string) => void;
  onAuthorize: (id: string) => void;
  onCertificate: (id: string) => void;
  onDelete: (id: string) => void;
  canVerify: boolean;
  canAuthorize: boolean;
  canAuthorizeRow: (verifiedBy: string | null | undefined) => boolean;
  formatDate: (dateStr: string | null | undefined) => string;
}

function BoardView({ submissionsByStatus, onView, onEdit, onVerify, onAuthorize, onCertificate, onDelete, canVerify, canAuthorize, canAuthorizeRow, formatDate }: BoardViewProps) {
  return (
    <div className="ops-board">
      {WORKFLOW_COLUMNS.map(col => {
        const subs = submissionsByStatus[col.id] || [];
        const status = STATUS_CONFIG[col.id] ?? { label: col.label, color: 'var(--color-text-light)', bg: 'var(--color-bg)' };

        return (
          <div key={col.id} className="ops-board-column">
            <div className="ops-board-column-header" style={{ borderTopColor: status.color }}>
              <span className="ops-board-column-title">{col.label}</span>
              <span className="ops-board-column-count">{subs.length}</span>
            </div>
            <div className="ops-board-column-body">
              {subs.length === 0 ? (
                <div className="ops-board-empty">No items</div>
              ) : (
                subs.slice(0, 20).map(sub => (
                  <div key={sub.id} className="ops-board-card" onClick={() => onView(sub.id)}>
                    <div className="ops-board-card-header">
                      <span className="ops-board-card-customer">{sub.display_customer}</span>
                    </div>
                    <div className="ops-board-card-meta">
                      <span>{formatDate(sub.service_date)}</span>
                      {sub.invoice_number && (
                        <span className="ops-split-chip-sm">{sub.invoice_number}</span>
                      )}
                    </div>
                    <div className="ops-board-card-meta">
                      <span>By: {sub.operator_name}</span>
                    </div>
                    <div className="ops-board-card-actions" onClick={e => e.stopPropagation()}>
                      {sub.submission_status === 'draft' && (
                        <button className="ops-board-action" onClick={() => onEdit(sub.id)}>Edit</button>
                      )}
                      {sub.submission_status === 'pending_verification' && canVerify && (
                        <button className="ops-board-action ops-board-action-primary" onClick={() => onVerify(sub.id)}>
                          Verify
                        </button>
                      )}
                      {sub.submission_status === 'pending_authorization' && canAuthorizeRow(sub.verified_by as string | null | undefined) && (
                        <button className="ops-board-action ops-board-action-primary" onClick={() => onAuthorize(sub.id)}>
                          Authorise
                        </button>
                      )}
                      {sub.submission_status === 'returned' && (
                        <button className="ops-board-action" onClick={() => onEdit(sub.id)}>Resubmit</button>
                      )}
                      {sub.submission_status === 'certified' && (
                        <button className="ops-board-action ops-board-action-primary" onClick={() => onCertificate(sub.id)}>
                          View Certificate
                        </button>
                      )}
                      {canAuthorize && sub.submission_status !== 'certified' && (
                        <button className="ops-board-action" onClick={() => onDelete(sub.id)}>Delete</button>
                      )}
                    </div>
                  </div>
                ))
              )}
              {subs.length > 20 && (
                <div className="ops-board-more">+{subs.length - 20} more</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// View Modal
interface ViewModalProps {
  submission: SubmissionDetail;
  onClose: () => void;
  onVerify: (selectedUserId: string) => void;
  onAuthorize: (selectedUserId: string) => void;
  onReturn: (stage: ReturnStage) => void;
  onEdit: () => void;
  onCertificate: () => void;
  canVerify: boolean;
  canAuthorize: boolean;
  formatDate: (dateStr: string | null | undefined) => string;
  adminUsers: AdminUser[];
  currentUserId: string;
  isSuperAdmin: boolean;
}

function ViewModal({ submission, onClose, onVerify, onAuthorize, onReturn, onEdit, onCertificate, canVerify, canAuthorize, formatDate, adminUsers = [], currentUserId, isSuperAdmin }: ViewModalProps) {
  // Verifier / authoriser default to the logged-in person. Super-admins
  // can override via a dropdown — everyone else acts as themselves.
  const [selectedVerifierId, setSelectedVerifierId] = useState(currentUserId);
  const [selectedAuthorizerId, setSelectedAuthorizerId] = useState(currentUserId);

  // Per-No acknowledgement state — the verifier / authoriser must tick
  // every "no" answer the operator gave before action buttons enable.
  const [acknowledgedNos, setAcknowledgedNos] = useState<Set<string>>(new Set());
  useEffect(() => {
    setAcknowledgedNos(new Set());
    setSelectedVerifierId(currentUserId);
    setSelectedAuthorizerId(currentUserId);
  }, [submission.id, currentUserId]);

  const operatorId = (submission.operator_id as string | undefined) || '';
  const operatorIsCurrentUser = operatorId && operatorId === currentUserId;

  // Verifier candidates: everyone with the verify permission minus the
  // operator. (Same-person guard at the data layer enforces it too.)
  const verifierOptions = adminUsers.filter(u => u.canVerify && u.id !== operatorId);
  // Authoriser candidates: everyone with the authorise permission minus
  // the person who verified this cert (the existing same-person rule).
  const authorizerOptions = adminUsers.filter(u => u.canAuthorize && u.id !== submission.verified_by);

  if (submission.loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
          <div className="placeholder-page">
            <div className="spinner" />
            <p>Loading submission...</p>
          </div>
        </div>
      </div>
    );
  }

  if (submission.error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
          <div className="empty-state">
            <p>Error: {submission.error}</p>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const sub = submission;
  const status = STATUS_CONFIG[sub.submission_status as string] ?? { label: 'Draft', color: 'var(--color-text-light)', bg: 'var(--color-bg)' };

  const showVerifyActions = sub.submission_status === 'pending_verification';
  const showAuthorizeActions = sub.submission_status === 'pending_authorization';
  const showEditAction = sub.submission_status === 'draft' || sub.submission_status === 'returned';
  const showCertificateAction = sub.submission_status === 'certified';

  let customerName = (sub.customer_name as string) || '\u2014';
  let serviceAddress = '\u2014';
  if (sub.service_location) {
    const parts = (sub.service_location as string).split(' - ');
    if (!sub.customer_name) {
      customerName = parts[0] || '\u2014';
    }
    serviceAddress = parts.slice(1).join(' - ') || '\u2014';
  }

  // Non-compliance signal \u2014 surfaces the operator's flag at the top of
  // the modal so the verifier cannot miss it, and changes the Authorise
  // button into a destructive-styled action with a confirmation prompt.
  const checklistData = (sub.checklist_data || {}) as Record<string, Record<string, unknown>>;
  const step8Data = checklistData.step8 || {};
  const operatorMarkedNonCompliant = step8Data.protocol_compliance === false;
  const nonComplianceReason = typeof step8Data.non_compliance_reason === 'string'
    ? (step8Data.non_compliance_reason as string)
    : '';

  // Every "no" answer the operator gave on steps 3-6. The reviewer must
  // tick each one before Verify / Authorise unlocks.
  const noAnswerKeys: string[] = [];
  for (const stepKey of ['step3', 'step4', 'step5', 'step6']) {
    const step = checklistData[stepKey];
    if (!step) continue;
    for (const key of Object.keys(step)) {
      if (!key.startsWith('q') || key.endsWith('_comment')) continue;
      const v = step[key];
      if (v === 'no' || v === false || v === 'false') {
        noAnswerKeys.push(`${stepKey}_${key}`);
      }
    }
  }
  const totalNos = noAnswerKeys.length;
  const allNosAcknowledged = noAnswerKeys.every(k => acknowledgedNos.has(k));
  const toggleNoAck = (key: string) => {
    setAcknowledgedNos(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Return banner \u2014 surfaces the reason at the top of the modal whenever
  // the cert has been bounced back and not yet re-actioned.
  const returnReason = typeof sub.return_reason === 'string' ? sub.return_reason : '';
  const showReturnBanner = !!returnReason && (
    sub.submission_status === 'returned' ||
    (sub.submission_status === 'pending_verification' && !sub.verified_at)
  );
  const returnedToLabel = sub.submission_status === 'returned' ? 'operator' : 'verifier';

  function handleAuthoriseClick() {
    if (operatorMarkedNonCompliant) {
      const reasonPreview = nonComplianceReason
        ? `\n\nOperator's stated reason:\n${nonComplianceReason}`
        : '\n\nOperator did not provide a reason.';
      const ok = window.confirm(
        `This certificate was flagged as NON-COMPLIANT by the operator.${reasonPreview}\n\nAuthorising will issue a certificate that publicly carries the non-compliance flag. Are you sure?`
      );
      if (!ok) return;
    }
    onAuthorize(selectedAuthorizerId);
  }

  // Print via hidden iframe — avoids popup-window restrictions in standalone
  // PWAs (where window.open + .close() can strand the user on a blank tab).
  const printSubmission = () => {
    const modalContent = document.getElementById('cert-modal-content')?.innerHTML || '';
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:-10000px;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      return;
    }

    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>Certification Submission</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; color: #333; }
        h2, h3, h4 { color: #722cbb; margin-top: 20px; }
        section { margin-bottom: 24px; page-break-inside: avoid; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; }
        @media print { body { padding: 20px; } button { display: none !important; } }
      </style>
      </head><body>
      <h1 style="color: #722cbb; border-bottom: 3px solid #722cbb; padding-bottom: 10px;">CleanMyBed Certification Submission</h1>
      ${modalContent}
      </body></html>`);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => iframe.remove(), 100);
    }, 100);
  };


  // Group equipment by type
  const groupedEquipment: Record<string, Record<string, unknown>[]> = {};
  if (sub.equipment_used && Array.isArray(sub.equipment_used)) {
    (sub.equipment_used as Record<string, unknown>[]).forEach(eq => {
      const type = (eq.equipment_type as string) || 'Other';
      if (!groupedEquipment[type]) groupedEquipment[type] = [];
      groupedEquipment[type].push(eq);
    });
  }

  const parseItems = (description: string | undefined | null): string[] => {
    if (!description) return [];
    return description.split(';').map(i => i.trim()).filter(Boolean);
  };

  const primaryItems = parseItems(sub.primary_asset_description as string);
  const additionalItems = parseItems(sub.additional_items_description as string);
  const sundryList = sub.sundries && Array.isArray(sub.sundries) ? sub.sundries as Record<string, unknown>[] : [];

  const stepTitles: Record<string, string> = {
    step3: 'HEPA Extraction Quality',
    step4: 'UVC Treatment Quality',
    step5: 'Dry & Chemical-Free Assurance',
    step6: 'Safety & Containment',
    step7: 'Service Notes',
    step8: 'Final Declaration',
    step3_pre_service_setup: 'Pre-Service Setup (Legacy)',
    step4_vacuum_equipment: 'Vacuum Equipment Check (Legacy)',
    step5_hepa_extraction: 'HEPA Extraction (Legacy)',
    step6_uvc_equipment: 'UVC Equipment Check (Legacy)',
    step7_uvc_treatment: 'UVC Treatment (Legacy)',
    step8_compliance_safety: 'Compliance & Safety (Legacy)',
    step9_service_notes: 'Service Notes (Legacy)',
    step10_operator_confirmation: 'Operator Confirmation (Legacy)'
  };

  const questionLabels: Record<string, Record<string, string>> = {
    step3: { q1: 'HEPA extraction completed', q2: 'Overlapping passes used', q3: 'All surfaces extracted' },
    step4: { q1: 'UVC treatment completed', q2: 'Correct duration/dosage', q3: 'Safety protocols followed' },
    step5: { q1: 'No moisture used', q2: 'No chemicals used' },
    step6: { q1: 'Material disposed correctly', q2: 'Appropriate PPE worn', q3: 'Room left clean and safe' },
    step7: { service_notes: 'Service Notes' },
    step8: { protocol_compliance: 'Protocol Compliance', signature_name: 'Signature', signed_at: 'Signed At' }
  };

  const stepOrder = [
    'step3', 'step4', 'step5', 'step6', 'step7', 'step8',
    'step3_pre_service_setup', 'step4_vacuum_equipment', 'step5_hepa_extraction',
    'step6_uvc_equipment', 'step7_uvc_treatment', 'step8_compliance_safety',
    'step9_service_notes', 'step10_operator_confirmation'
  ];

  const checklist = (sub.checklist_data || {}) as Record<string, Record<string, unknown>>;

  const formatChecklistValue = (value: unknown) => {
    if (value === 'yes' || value === true || value === 'true') {
      return <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{'\u2713'} Yes</span>;
    }
    if (value === 'no' || value === false || value === 'false') {
      return <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{'\u2717'} No</span>;
    }
    return String(value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h2>Submission Details</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={printSubmission} className="btn btn-secondary btn-sm">Print Inspection Report</button>
            {sub.submission_status === 'certified' && (
              <button onClick={onCertificate} className="btn btn-primary btn-sm">View Certificate</button>
            )}
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div id="cert-modal-content" className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gap: '24px' }}>

            {showReturnBanner && (
              <div style={{
                background: 'var(--color-warning-bg)',
                border: '1px solid #d97706',
                borderRadius: 'var(--radius)',
                padding: '0.75rem 1rem',
              }}>
                <div style={{ fontWeight: 600, color: 'var(--color-warning-text)', marginBottom: '0.25rem' }}>
                  ↩ This submission was returned to the {returnedToLabel}
                </div>
                <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-warning-text)', whiteSpace: 'pre-wrap' }}>
                  <strong>Reason:</strong> {returnReason}
                </div>
              </div>
            )}

            {operatorMarkedNonCompliant && (
              <div style={{
                background: 'var(--color-error-bg)',
                border: '1px solid #dc2626',
                borderRadius: 'var(--radius)',
                padding: '0.75rem 1rem',
              }}>
                <div style={{ fontWeight: 600, color: 'var(--color-error-text)', marginBottom: '0.25rem' }}>
                  ⚠ Operator flagged this submission as NON-COMPLIANT
                </div>
                {nonComplianceReason ? (
                  <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-error-text)', whiteSpace: 'pre-wrap' }}>
                    <strong>Reason:</strong> {nonComplianceReason}
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-error-text)', fontStyle: 'italic' }}>
                    No reason provided. Return to operator for explanation before authorising.
                  </div>
                )}
              </div>
            )}

            <div className="view-section">
              <div className="section-title">Service Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><strong>Reference:</strong> {(sub.invoice_number as string) || '\u2014'}</div>
                <div><strong>Date:</strong> {formatDate(sub.service_date as string)}</div>
                <div><strong>Customer Name:</strong> {customerName}</div>
                <div><strong>Service Location:</strong> {serviceAddress}</div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>Status:</strong>{' '}
                  <StatusBadge bg={status.bg} color={status.color}>{status.label}</StatusBadge>
                </div>
              </div>
            </div>

            <div className="view-section">
              <div className="section-title">Service Team</div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Primary Operator:</strong> {sub.operator_name}{' '}
                <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">{'\u2713'} ACCREDITED</StatusBadge>
              </div>
              {(() => {
                const members = Array.isArray(sub.team_members) ? sub.team_members as Array<{ first_name: string; last_name: string }> : [];
                if (members.length === 0) return null;
                return (
                  <div style={{ marginTop: '8px' }}>
                    <strong>Team Members:</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {members.map((m, i) => (
                        <li key={i} style={{ marginBottom: '2px' }}>{String(m.first_name || '')} {String(m.last_name || '')}</li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>

            {Object.keys(groupedEquipment).length > 0 && (
              <div className="view-section">
                <div className="section-title">Equipment Used</div>
                {Object.entries(groupedEquipment).map(([type, items]) => (
                  <div key={type} style={{ marginBottom: '16px' }}>
                    <strong className="text-primary">
                      {type}: <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">{'\u2713'} CERTIFIED</StatusBadge>
                    </strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {items.map((eq, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>
                          {(eq.model as string) || 'Unknown Model'} ({(eq.serial_number as string) || '\u2014'})
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {(primaryItems.length > 0 || additionalItems.length > 0 || sundryList.length > 0) && (
              <div className="view-section">
                <div className="section-title">Items Serviced</div>
                {primaryItems.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong className="text-primary">Primary Assets (Beds):</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {primaryItems.map((item, idx) => {
                        const match = item.match(/(.+?),\s*(.+)/);
                        if (match?.[1] && match?.[2]) return <li key={idx} style={{ marginBottom: '4px' }}>{match[1].trim()} ({match[2].trim()})</li>;
                        return <li key={idx} style={{ marginBottom: '4px' }}>{item}</li>;
                      })}
                    </ul>
                  </div>
                )}
                {additionalItems.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong className="text-primary">Additional Items:</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {additionalItems.map((item, idx) => {
                        const match = item.match(/(.+?),\s*(.+)/);
                        if (match?.[1] && match?.[2]) return <li key={idx} style={{ marginBottom: '4px' }}>{match[1].trim()} ({match[2].trim()})</li>;
                        return <li key={idx} style={{ marginBottom: '4px' }}>{item}</li>;
                      })}
                    </ul>
                  </div>
                )}
                {sundryList.length > 0 && (
                  <div>
                    <strong className="text-primary">Other Items:</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {sundryList.map((sundry, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>
                          {(sundry.item_type as string) || 'Unknown Item'} ({(sundry.quantity as number) || 1})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {Object.keys(checklist).length > 0 && (
              <div className="view-section">
                <div className="section-title">
                  Checklist Responses
                  {totalNos > 0 && (showVerifyActions || showAuthorizeActions) && (
                    <span style={{ fontSize: 'var(--text-base)', fontWeight: 400, marginLeft: '12px', color: allNosAcknowledged ? 'var(--color-success)' : 'var(--color-error)' }}>
                      {allNosAcknowledged
                        ? `✓ All ${totalNos} No answers acknowledged`
                        : `${acknowledgedNos.size} of ${totalNos} No answers acknowledged`}
                    </span>
                  )}
                </div>
                {stepOrder.map(stepKey => {
                  const stepData = checklist[stepKey];
                  if (!stepData) return null;
                  return (
                    <div key={stepKey} style={{ marginBottom: '20px' }}>
                      <h4 style={{ color: 'var(--color-text)', marginBottom: '10px' }}>{stepTitles[stepKey] || stepKey}</h4>
                      <div style={{ display: 'grid', gap: '8px', paddingLeft: '16px' }}>
                        {Object.keys(stepData).map(key => {
                          if (key === 'general_comments' && stepData[key]) {
                            return <div key={key}><strong>General Comments:</strong> {String(stepData[key])}</div>;
                          }
                          if (!key.endsWith('_comment') && stepData[key] !== null && stepData[key] !== undefined) {
                            const label = (questionLabels[stepKey] && questionLabels[stepKey][key])
                              || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            const value = stepData[key];
                            const comment = stepData[key + '_comment'] as string | undefined;
                            const isNo = value === 'no' || value === false || value === 'false';
                            const ackKey = `${stepKey}_${key}`;
                            const isQuestion = key.startsWith('q');
                            return (
                              <div key={key}>
                                <strong>{label}:</strong> {formatChecklistValue(value)}
                                {comment ? <div style={{ color: 'var(--color-text-light)', marginLeft: '20px', fontStyle: 'italic' }}>{comment}</div> : null}
                                {isNo && isQuestion && (showVerifyActions || showAuthorizeActions) && (
                                  <label style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                    marginLeft: '20px',
                                    marginTop: '6px',
                                    padding: '6px 10px',
                                    background: acknowledgedNos.has(ackKey) ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
                                    border: `1px solid ${acknowledgedNos.has(ackKey) ? 'var(--color-success)' : 'var(--color-error)'}`,
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-base)',
                                    cursor: 'pointer',
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={acknowledgedNos.has(ackKey)}
                                      onChange={() => toggleNoAck(ackKey)}
                                      style={{ marginTop: '2px' }}
                                    />
                                    <span style={{ color: acknowledgedNos.has(ackKey) ? 'var(--color-success-text)' : 'var(--color-error-text)' }}>
                                      I&apos;ve read this and accept the operator&apos;s explanation.
                                    </span>
                                  </label>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="view-section" style={{ borderBottom: 'none' }}>
              <div className="section-title">Workflow History</div>
              <div style={{ display: 'grid', gap: '16px' }}>
                {sub.created_at && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">{'\u2713'} Submitted</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.operator_name}<br />
                    <strong>Date:</strong> {formatDate(sub.created_at as string)}
                  </div>
                )}
                {sub.verified_by && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">{'\u2713'} Verified</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.verifier_name}<br />
                    <strong>Date:</strong> {formatDate(sub.verified_at as string)}
                    {sub.verification_notes && <><br /><strong>Notes:</strong> {sub.verification_notes}</>}
                  </div>
                )}
                {sub.authorized_by && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">{'\u2713'} Authorised</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.authorizer_name}<br />
                    <strong>Date:</strong> {formatDate(sub.authorized_at as string)}
                    {sub.authorization_notes && <><br /><strong>Notes:</strong> {sub.authorization_notes}</>}
                  </div>
                )}
                {sub.endorsed_by && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">{'\u2713'} Endorsed</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.endorser_name}<br />
                    <strong>Date:</strong> {formatDate(sub.endorsed_at as string)}
                    {sub.endorsement_notes && <><br /><strong>Notes:</strong> {sub.endorsement_notes}</>}
                  </div>
                )}
              </div>
            </div>

            {Array.isArray(sub.audit_entries) && (sub.audit_entries as AuditEntry[]).length > 0 && (
              <div className="view-section" style={{ borderBottom: 'none' }}>
                <div className="section-title">Audit Trail</div>
                <p className="text-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 0 }}>
                  Append-only log of admin corrections on this submission. Visible for AFSA defensibility.
                </p>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {(sub.audit_entries as AuditEntry[]).map(entry => (
                    <div
                      key={entry.id}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--color-warning-bg)',
                        border: '1px solid #f59e0b',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--text-base)',
                      }}
                    >
                      <div style={{ fontWeight: 600, color: 'var(--color-warning-text)' }}>
                        {entry.action.replace(/_/g, ' ')}
                        {entry.field_path ? <span style={{ fontWeight: 400 }}> \u2014 {entry.field_path}</span> : null}
                      </div>
                      <div style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-md)' }}>
                        {entry.user_name || 'System'} on {formatDate(entry.created_at)}
                      </div>
                      {entry.reason && (
                        <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{entry.reason}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>

          {showEditAction && (
            <button className="btn btn-primary" onClick={onEdit}>
              {sub.submission_status === 'returned' ? 'Edit & Resubmit' : 'Edit'}
            </button>
          )}

          {showVerifyActions && (() => {
            // The operator cannot verify their own service. Non-super-admin
            // operators see a clear message and can't proceed. Super-admins
            // get an override dropdown to nominate someone else.
            const operatorBlocked = operatorIsCurrentUser && !isSuperAdmin;
            const ackPending = totalNos > 0 && !allNosAcknowledged;
            const verifyDisabled = !canVerify || operatorBlocked || ackPending;
            const verifyButtonLabel = ackPending
              ? `Verify (${acknowledgedNos.size} of ${totalNos} acknowledged)`
              : 'Verify';
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
                  {operatorBlocked ? (
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-error)' }}>
                      You are the operator — another manager must verify this submission.
                    </span>
                  ) : isSuperAdmin ? (
                    <>
                      <label style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>Verified by (admin override):</label>
                      <SearchSelect
                        options={verifierOptions.map(u => ({ value: u.id, label: u.name }))}
                        value={selectedVerifierId || null}
                        onChange={setSelectedVerifierId}
                        width="inline"
                      />
                    </>
                  ) : (
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>
                      Verifying as: <strong>{adminUsers.find(u => u.id === currentUserId)?.name || 'you'}</strong>
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => onReturn('operator')}
                  disabled={!canVerify}
                >
                  Return to Operator
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => onVerify(selectedVerifierId)}
                  disabled={verifyDisabled}
                  title={ackPending ? `Tick each No answer (${totalNos - acknowledgedNos.size} remaining)` : ''}
                >
                  {verifyButtonLabel}
                </button>
              </>
            );
          })()}

          {showAuthorizeActions && (() => {
            const isOwnVerification = submission.verified_by === currentUserId;
            const ackPending = totalNos > 0 && !allNosAcknowledged;
            const blocked = !canAuthorize || (isOwnVerification && !isSuperAdmin) || ackPending;
            const authoriseLabel = ackPending
              ? `Authorise (${acknowledgedNos.size} of ${totalNos} acknowledged)`
              : operatorMarkedNonCompliant
                ? 'Authorise as Non-Compliant'
                : 'Authorise';
            const authoriseStyle: CSSProperties = operatorMarkedNonCompliant && !ackPending
              ? { background: '#dc2626', borderColor: '#dc2626', color: 'var(--color-white)' }
              : {};
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
                  {isOwnVerification && !isSuperAdmin ? (
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>
                      You verified this submission — someone else must authorise it.
                    </span>
                  ) : isSuperAdmin ? (
                    <>
                      <label style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>Authorised by (admin override):</label>
                      <SearchSelect
                        options={authorizerOptions.map(u => ({ value: u.id, label: u.name }))}
                        value={selectedAuthorizerId || null}
                        onChange={setSelectedAuthorizerId}
                        width="inline"
                      />
                    </>
                  ) : (
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>
                      Authorising as: <strong>{adminUsers.find(u => u.id === currentUserId)?.name || 'you'}</strong>
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => onReturn('verifier')}
                  disabled={!canAuthorize}
                  title="Send back to the verifier for another look"
                >
                  Return to Verifier
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => onReturn('operator')}
                  disabled={!canAuthorize}
                  title="Send all the way back to the operator"
                >
                  Return to Operator
                </button>
                <button
                  className={operatorMarkedNonCompliant ? 'btn btn-secondary' : 'btn btn-primary'}
                  onClick={handleAuthoriseClick}
                  disabled={blocked}
                  style={authoriseStyle}
                  title={ackPending ? `Tick each No answer (${totalNos - acknowledgedNos.size} remaining)` : ''}
                >
                  {authoriseLabel}
                </button>
              </>
            );
          })()}

          {showCertificateAction && (
            <button className="btn btn-primary" onClick={onCertificate}>View Certificate</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Certification;
